import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Heart } from "lucide-react";
import MarketplaceNav from "@/components/coachly/MarketplaceNav";
import CoachCard from "@/components/coachly/CoachCard";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// SavedCoaches — private page. Requires a signed-in Google user.
export default function SavedCoaches() {
  const { user, openSignIn, loading: authLoading } = useAuth();
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user?.email) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/favorites`, { params: { email: user.email } })
      .then((r) => { if (!cancelled) setCoaches(r.data.coaches || []); })
      .catch(() => { if (!cancelled) setCoaches([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user?.email]);

  return (
    <div data-testid="saved-coaches-page" className="bg-white min-h-screen">
      <MarketplaceNav />
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-10">
        <div className="max-w-3xl">
          <div className="eyebrow inline-flex items-center gap-2">
            <Heart className="w-3.5 h-3.5 text-[#E11D48]" fill="currentColor" strokeWidth={2} />
            § Saved coaches
          </div>
          <h1 className="mt-4 font-display font-medium text-[44px] sm:text-[64px] leading-[0.98] tracking-[-0.05em] text-slate-900">
            Your <span className="italic font-light text-[#2563EB]">shortlist.</span>
          </h1>
          <p className="mt-4 text-slate-600 text-lg max-w-[54ch] leading-relaxed">
            The coaches you've saved. Come back any time to compare and book.
          </p>
          {coaches.length >= 2 && (
            <Link
              to="/compare"
              data-testid="saved-compare-cta"
              className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-slate-900 hover:bg-[#2563EB] text-white text-[13px] font-semibold transition-colors"
            >
              Compare side-by-side ({coaches.length}) →
            </Link>
          )}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24">
        {authLoading ? (
          <div className="text-[13px] text-slate-400">Loading…</div>
        ) : !user ? (
          <div
            data-testid="saved-signin-required"
            className="max-w-md rounded-2xl border border-slate-200 p-6"
          >
            <div className="eyebrow">§ Sign in</div>
            <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">
              Your saved coaches live behind Google sign-in so nobody else can see your shortlist.
            </p>
            <button
              type="button"
              data-testid="saved-signin-google"
              onClick={() => openSignIn("Sign in to see your saved coaches.")}
              className="mt-4 h-11 px-5 rounded-full bg-slate-900 hover:bg-[#2563EB] text-white text-[13px] font-semibold transition-colors"
            >
              Continue with Google →
            </button>
          </div>
        ) : loading ? (
          <div data-testid="saved-loading" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="animate-pulse rounded-2xl border border-slate-100 overflow-hidden">
                <div className="aspect-[4/5] bg-slate-100" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-slate-100 rounded w-2/3" />
                  <div className="h-3 bg-slate-100 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : coaches.length === 0 ? (
          <div data-testid="saved-empty" className="text-center py-24 max-w-lg mx-auto">
            <div className="eyebrow">§ Nothing yet</div>
            <h3 className="mt-4 font-display text-3xl tracking-tight text-slate-900">
              No saved coaches — yet.
            </h3>
            <p className="mt-2 text-slate-500">
              Tap the heart on any coach card or profile to build your shortlist.
            </p>
            <Link
              to="/marketplace"
              className="mt-6 inline-flex h-11 px-5 rounded-full bg-slate-900 hover:bg-[#2563EB] text-white text-[13px] font-semibold transition-colors"
            >
              Browse the marketplace
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="saved-grid">
            {coaches.map((c, i) => (
              <CoachCard
                key={c.id}
                coach={c}
                index={i}
                onUnsaved={(slug) => setCoaches((prev) => prev.filter((x) => x.slug !== slug))}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
